// Volumetric cumulus for the near field of the true-scale local scene.
//
// Fair-weather cumulus over the Cape (humilis to congestus, flat bases near
// 1.2 to 1.6 km, tops from 2 to 4 km) raymarched through a spherical shell
// in real metres, lit by the environment's key light through the air, and
// composited into the local pass so terrain, the launch complex, the vessel
// and its exhaust all sort against them. Cumulus stays volumetric all the
// way to the geometric horizon; only the orbital altitude transition changes
// representation. The flat cumulus deck is reserved for the low quality tier.
//
// Per frame, before the composer runs (the 'frame' event):
//   1. Depth prepass: the local scene's depth-writing geometry, as distance
//      from the camera, into a full-resolution R32F target. The composer's
//      own depth is multisampled and in use at that point in the frame;
//      reading it would mean breaking its render pass mid-frame.
//   2. Cloud shadow at the march's resolution: the direct sun reaching the
//      surface under each texel, read off the weather field along the light
//      (continuous in space and time, so it cannot flicker).
//   3. Beauty march at reduced resolution with per-frame sub-texel jitter
//      and an interleaved-gradient ray offset (MRT: radiance/transmittance
//      and entry/exit/representative/scene distances).
//   4. Temporal resolve: reprojection through the representative depth in
//      the Earth-fixed frame, disocclusion rejection, variance clipping.
// Then inside the local pass, two fullscreen quads in the transparent list
// just after the terrain: the composite (depth-aware upsample of the cloud
// and its shadow, which darkens surfaces) and a depth stamp for the
// transparents drawn later.
//
// Every noise lives in world space (Earth-fixed metres plus wind), so the
// only screen-space pattern is the per-frame jitter the history averages
// away; with the camera still the result converges and holds.

import * as THREE from 'three';
import { getEarthWeather } from '../earthweather.js';
import { buildCloudNoise } from './cloudnoise.js';
import {
  FULLSCREEN_VERT, PREPASS_VERT, PREPASS_FRAG, MARCH_FRAG, RESOLVE_FRAG,
  SHADOW_FRAG, COMPOSITE_VERT, COMPOSITE_FRAG, STAMP_FRAG
} from './cloudshaders.js';
import { approximateSkyColor } from './sky.js';
import { OKLAB_MIX_GLSL } from '../shaders/oklab.js';
import { EARTH_WEATHER_UV_GLSL } from '../shaders/earthweather.js';
import { EARTH_CLOUD_TOP_GLSL } from '../shaders/earthcloudtop.js';
import { CLOUD_IMAGERY_GLSL, cloudImageryUniforms } from './cloudimagery.js';
import { SITES, siteBasis } from './terrain.js';
import { withDepthLayer } from './depth.js';
import { DECK_HIGH_FADE } from './clouds.js';
import { nightMapUniforms } from './nightimagery.js';

// Normal-viewer altitude band (metres) over which the volumes hand the clouds
// to the globe, which draws the full-resolution cloud reference.
export const GLOBAL_VOLUME_FADE = [100000, 200000];

// Look and physics. Distances in metres, speeds in m/s. The field and its
// lighting follow TAKEN's volumetric clouds (src/world/VolumetricClouds.ts,
// its shipped fair-weather cumulus preset), on this mode's spherical shell.
const CLOUD = {
  coverage: 0.20,          // threshold on the placement field where the map is average, not a sky fraction
  mapGain: 0.12,           // how strongly the orbital cloud map raises or lowers it (x2 in the shader)
  base: 1400,              // mean cloud base
  baseVar: 380,            // base swing with the macro front (busier air bases lower)
  // Broad, shallow fair-weather masses, with deeper crowns in busy air.
  // TAKEN's default: about 900 m of slab against masses about 760 m across,
  // so a puff can carve a tall cumulus rather than a pancake.
  depthMin: 650,
  depthMax: 1000,          // congestus under the busiest air
  cloudType: 1.0,          // 0 a flat stratus deck, 1 rounded cumulus
  erosion: 1.4,           // how deeply the up-rez noise carves the edges
  sigma: 0.044,            // extinction per metre at density 1 (TAKEN: 0.032 x density 1.38)
  tiles: [14000, 574, 176], // weather, shape, detail tile sizes
  curl: 0.34,
  curlPeriod: 14000 / (12 * .9),
  // Along-wind compression of the placement: cloud streets. One constant for
  // every weather read, because the CPU folds the camera's offset with it
  // (alongFold); 0.78 is the full-street value the per-point compression
  // this replaced gave near the ground.
  along: 0.78,
  shear: 0.102,            // downwind lean of the tops, per metre above the base
  // March bounds: the lowest base to the tallest top the field can grow.
  layer: [1150, 3600],
  shellH: 1400,            // weather sampling shell
  // Winds over the Cape on a summer afternoon: the sea breeze pushes the
  // low cumulus west-north-west. Shape and detail move at slightly
  // different speeds from the weather, so each cloud keeps changing shape
  // as it drifts.
  wind: { weather: [-4.4, 1.6], shape: [-5.0, 2.0], detail: [-6.2, 2.8], rise: 0.7 },
  // The ground shadow's range near the ground (it reaches the horizon from
  // orbit, with the far look), kept independently of cloud visibility.
  fade: [85000, 125000],
  // Direct handoff to orbital weather; never expose the flat cumulus deck
  // between the volume and the orbital shell.
  altFade: DECK_HIGH_FADE,
  ms: [0, 0.6, 0.75, 0],   // extinction and phase attenuation per scattering order
  phase: [0.78, 0, 0, 1.0], // anisotropy, unused, unused, powder
  msGain: 0.7,             // TAKEN's 3.05 against a sun about 2.3x brighter here, and no camera exposure to follow
  silver: 0.5,             // silver lining on sunward edges (TAKEN 1.65 against a sun about 2.3x brighter here)
  ambSky: 0.45,
  ambGround: 1.0,
  groundAlbedo: 0.11,
  shadowStrength: 0.55,    // share of a sunlit surface's light that is direct
  // Towns light cloud bases faintly from below at night: this much over a
  // town (the 8K's light there, about 0.8 over the Cape's towns read 7 km
  // wide), in proportion to the lights under the cloud and none where there
  // are none (cityGlowAt in cloudshaders.js). It was once this constant on
  // every cloud everywhere, so cumulus over open sea glowed sodium orange.
  cityGlow: [0.032, 0.022, 0.013],
  cityGlowRef: 0.8,
  sunIntensity: 2.6,       // environment.js SUN_INTENSITY (noon, sea level)
  betaR: [5.8e-6, 13.5e-6, 33.1e-6],
  betaM: 16e-6,
  betaO: [0.650e-6, 1.881e-6, 0.085e-6]
};

const SUN_WARM = new THREE.Color(0xfff4e6);
const HALTON = [];
for (let i = 1; i <= 16; i++) {
  const h = (b) => { let f = 1, r = 0, n = i; while (n > 0) { f /= b; r += f * (n % b); n = Math.floor(n / b); } return r; };
  HALTON.push([h(2), h(3)]);
}
const FAR_CLEAR = 1e8;

// Airmass function matching the shader's (Schueler's approximation).
function chapman(x, mu) {
  const c = Math.sqrt(1.5707963 * x);
  if (mu >= 0) return c / (c * mu + 1);
  const x0 = x * Math.sqrt(Math.max(1 - mu * mu, 0));
  const c0 = Math.sqrt(1.5707963 * x0);
  return 2 * c0 * Math.exp(Math.min(x - x0, 60)) - c / (1 - c * mu);
}
// Transmittance of the air from altitude h toward a light at cos-zenith mu.
function airTransmittance(h, mu, R, out) {
  const r = R + h;
  const chR = chapman(r / 8000, mu), chM = chapman(r / 1200, mu), chO = chapman((R + 25000) / 15000, mu);
  const eR = 8000 * Math.exp(-h / 8000) * chR;
  const eM = CLOUD.betaM * 1.1 * 1200 * Math.exp(-h / 1200) * chM;
  out.r = Math.exp(-(CLOUD.betaR[0] * eR + eM + CLOUD.betaO[0] * 15000 * chO));
  out.g = Math.exp(-(CLOUD.betaR[1] * eR + eM + CLOUD.betaO[1] * 15000 * chO));
  out.b = Math.exp(-(CLOUD.betaR[2] * eR + eM + CLOUD.betaO[2] * 15000 * chO));
  return out;
}

const frac = (x) => x - Math.floor(x);

// The cloud tops (src/shaders/earthcloudtop.js) round the camera, for the
// volume to carve once it follows the map (weatherReg, cloudshaders.js) and
// to light the way the shell and the globe do from orbit (cloudFarLight):
// R the opacity as a share of a core's, G the lit top's luminance times it.
// Three cascades of 1024 texels, 200 m, 800 m and 3.2 km a texel north to
// south (about 100, 400 and 1600 km each way). Each is a window of one
// global latitude and longitude lattice, centred on the camera to the
// nearest texel, so a texel is always the same place and holds the carve
// read at that texel's footprint: the place's opacity filtered to it,
// whatever the camera does. The volume reads the cascade its footprint
// wants.
const TOPS = { size: 1024, texel: [200, 800, 3200] };
// Built on first use: cloudimagery.js reaches this module back through
// earthclose.js, so its exports are not ready while this one loads.
const topsFrag = () => /* glsl */`
  precision highp float;
  varying vec2 vUv;
  uniform sampler2D uClouds;
  uniform vec2 uTexel;
  uniform float uSharpen;
  uniform float uOpacity;
  uniform float uWeatherTime;
  uniform float uLat0;       // the window's centre texel (radians)
  uniform float uLon0;
  uniform float uStep;       // radians a texel
  uniform float uSize;
  uniform float uTexelM;
  uniform vec3 uSunFixed;    // the sun in the Earth-fixed frame
  ${OKLAB_MIX_GLSL}
  ${EARTH_WEATHER_UV_GLSL}
  ${EARTH_CLOUD_TOP_GLSL}
  ${CLOUD_IMAGERY_GLSL}
  void main() {
    vec2 d = (vUv - 0.5) * uSize * uStep;
    float lat = clamp(uLat0 + d.y, -1.5707, 1.5707), lon = uLon0 + d.x;
    vec3 N = vec3(cos(lat) * cos(lon), sin(lat), -cos(lat) * sin(lon));
    // The map's address exactly as the orbital shell takes it: the weather's
    // warp and drift, longitude wrapped about the window's own.
    vec3 W = earthWeatherWarp(N, uWeatherTime);
    float wLat = asin(clamp(W.y, -1.0, 1.0));
    float wLon = atan(-W.z, W.x) - uLon0;
    wLon -= 6.2831853 * floor(wLon / 6.2831853 + 0.5);
    vec2 uv = vec2((wLon + uLon0) / 6.2831853 + 0.5 - earthWeatherDrift(wLat, uWeatherTime), 0.5 + wLat / 3.1415927);
    float lonD = (uv.x - 0.5) * 6.2831853;
    vec3 Nd = vec3(cos(wLat) * cos(lonD), sin(wLat), -cos(wLat) * sin(lonD));
    CloudMapRead cr = cloudMapRead(uClouds, uv, uTexel, uSharpen);
    CloudMapPoint cm = cloudPagedMapPoint(uClouds, uv, uTexel, cr, wLat, uOpacity);
    CloudTop ct = cloudTopField(Nd, cm, uTexelM, uCloudTopQuality > 0.5 ? 3 : 2);
    // The opacity as a share of a core's: the volume carves its density
    // from it, so a translucent rim stays a wisp and a core a solid mass.
    float share = ct.alpha / max(cm.thick, 1e-3);
    // The top's light as orbitclouds.js computes it, with the sun in the
    // tangent frame of the real point and no veil (the volume puts its own
    // air in front): relief, a deck's grey, the mass shading, the day ramp.
    vec3 east = normalize(vec3(N.z, 0.0, -N.x) + vec3(1e-5));
    vec3 north = cross(N, east);
    float sf = dot(N, uSunFixed);
    vec3 sT = vec3(dot(uSunFixed, east), dot(uSunFixed, north), sf);
    float mapM = uTexel.y * 3.1415927 * CT_RADIUS;
    vec3 col = cloudTopColour(ct, sT, vec3(0.0), 0.0) * cloudMassShade(uClouds, uv, cm, sT, max(log2(uTexelM / mapM), 0.0));
    float lum = dot(col, vec3(0.2126, 0.7152, 0.0722)) * smoothstep(-0.10, 0.22, sf);
    gl_FragColor = vec4(share, share * clamp(lum, 0.0, 1.0), 0.0, 1.0);
  }
`;

export function createVolumetricClouds(G) {
  const { ctx, R, bodies, localScene, localCamera, camInertial } = G;
  const renderer = ctx.renderer;
  const earthR = bodies.get('earth').radius;

  // Winds in the Earth-fixed frame, from the launch site's east and north.
  const site = SITES.earth[0];
  const basis = siteBasis(site.lat * Math.PI / 180, site.lon * Math.PI / 180);
  const windVec = (en, rise = 0) => new THREE.Vector3()
    .addScaledVector(basis.east, en[0]).addScaledVector(basis.north, en[1]).addScaledVector(basis.up, rise);
  const WIND = {
    weather: windVec(CLOUD.wind.weather),
    shape: windVec(CLOUD.wind.shape),
    detail: windVec(CLOUD.wind.detail, CLOUD.wind.rise)
  };
  // The wind's own axes: the placement field is compressed along the first
  // (cloud streets), the tops lean along it, and the de-tile offsets the
  // weather carries are laid out in both.
  const windT = WIND.weather.clone().normalize();
  const windB = new THREE.Vector3().crossVectors(basis.up, windT).normalize();
  // Compressing the placement domain along the wind means compressing the
  // whole lookup, offset included, and by the same factor the shader folds
  // the camera-relative part with (uField.x): only then does a weather read
  // depend on the ground point alone.
  const alongFold = (v, out) => out.copy(v).addScaledVector(windT, v.dot(windT) * (CLOUD.along - 1));

  // Fullscreen pass plumbing.
  const fsScene = new THREE.Scene();
  const fsCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const quadGeo = new THREE.PlaneGeometry(2, 2);
  const fsQuad = new THREE.Mesh(quadGeo);
  fsQuad.frustumCulled = false;
  fsScene.add(fsQuad);

  const U = {
    // field
    uWeather: { value: null }, uShape: { value: null }, uDetail: { value: null }, uMap: { value: null },
    uWeatherOff: { value: new THREE.Vector3() }, uShapeOff: { value: new THREE.Vector3() }, uDetailOff: { value: new THREE.Vector3() },
    uTiles: { value: new THREE.Vector3(...CLOUD.tiles) },
    uMapUV: { value: new THREE.Vector2() }, uDuDp: { value: new THREE.Vector3() }, uDvDp: { value: new THREE.Vector3() }, uMapOn: { value: 0 }, uMapPixelAngle: { value: 0 },
    uUp: { value: new THREE.Vector3(0, 1, 0) }, uCamR: { value: earthR }, uCamAlt: { value: 0 }, uShellH: { value: CLOUD.shellH },
    uToSurf: { value: new THREE.Matrix3() },
    uCloud: { value: new THREE.Vector4() }, uCloud2: { value: new THREE.Vector4() }, uDetailOn: { value: 1 },
    uCurlOff: { value: new THREE.Vector3() }, uCurl: { value: new THREE.Vector3(CLOUD.curl, CLOUD.curlPeriod, .9) },
    uWindT: { value: windT }, uWindB: { value: windB }, uField: { value: new THREE.Vector2(CLOUD.along, CLOUD.shear) },
    // march
    uSceneDist: { value: null }, uFullSize: { value: new THREE.Vector2(1, 1) }, uK: { value: 2 },
    uJitter: { value: new THREE.Vector2() }, uFrame: { value: 0 },
    uInvProj: { value: new THREE.Matrix4() }, uViewToSurf: { value: new THREE.Matrix3() },
    uSphereC: { value: new THREE.Vector4() }, uLayer: { value: new THREE.Vector2(...CLOUD.layer) },
    uFade: { value: new THREE.Vector2(...CLOUD.fade) }, uOpacity: { value: 1 }, uGlobalClouds: { value: G.globalClouds ? 1 : 0 }, uCloudDeckOpacity: { value: 0.85 },
    uSteps: { value: new THREE.Vector4(96, 40, 0.012, 420) }, uLightSteps: { value: 8 }, uLightFar: { value: 1500 }, uPixelAngle: { value: 0.001 }, uMarchDebug: { value: 0 },
    uFarLook: { value: 0 }, uFarColour: { value: 0 },
    uLightDir: { value: new THREE.Vector3(0, 1, 0) }, uLightE: { value: new THREE.Vector3(1, 1, 1) },
    uLightT: { value: new THREE.Vector3(1, 0, 0) }, uLightB: { value: new THREE.Vector3(0, 0, 1) },
    uAmbTop: { value: new THREE.Vector3() }, uAmbBottom: { value: new THREE.Vector3() },
    // The towns' light from below: the globe's 8K night map, and the glow per
    // unit of its light (rgb, night included) with the mip it is read at (w).
    uNightGlowMap: nightMapUniforms.uNightMap8, uCityGlow: { value: new THREE.Vector4(0, 0, 0, 0.5) },
    uSigma: { value: CLOUD.sigma }, uMS: { value: new THREE.Vector4(...CLOUD.ms) }, uMSGain: { value: CLOUD.msGain },
    uPhase: { value: new THREE.Vector4(...CLOUD.phase) }, uSilver: { value: CLOUD.silver },
    uBetaR: { value: new THREE.Vector3(...CLOUD.betaR) }, uBetaM: { value: CLOUD.betaM }, uBetaO: { value: new THREE.Vector3(...CLOUD.betaO) },
    uPlanetR: { value: earthR },
    uTops0: { value: null }, uTops1: { value: null }, uTops2: { value: null },
    uTopsLat0: { value: new THREE.Vector3() }, uTopsLon0: { value: new THREE.Vector3() },
    uTopsStep: { value: new THREE.Vector3(...TOPS.texel.map(t => t / earthR)) }, uTopsSize: { value: TOPS.size }, uTopsOn: { value: 0 },
    uFog: { value: new THREE.Vector4() }, uFogColor: { value: new THREE.Color() }, uFogSun: { value: new THREE.Vector3(0, 1, 0) }
  };

  const raw = (frag, uniforms, extra = {}) => new THREE.RawShaderMaterial({
    glslVersion: THREE.GLSL3, vertexShader: FULLSCREEN_VERT, fragmentShader: frag, uniforms,
    depthTest: false, depthWrite: false, ...extra
  });

  const marchMat = raw(MARCH_FRAG, U);
  const resolveU = {
    uCurA: { value: null }, uCurB: { value: null }, uHistA: { value: null }, uHistB: { value: null },
    uLowSize: { value: new THREE.Vector2(1, 1) }, uFullSize: U.uFullSize, uK: U.uK, uAlpha: { value: 0.1 },
    uHistoryValid: { value: 0 }, uInvProj: U.uInvProj, uViewToSurf: U.uViewToSurf,
    uPrevSurfToView: { value: new THREE.Matrix3() }, uPrevProj: { value: new THREE.Matrix4() },
    uCamDelta: { value: new THREE.Vector3() }, uWindStep: { value: new THREE.Vector3() }
  };
  const resolveMat = raw(RESOLVE_FRAG, resolveU);

  const compU = {
    uCloudA: { value: null }, uCloudB: { value: null }, uSceneDist: U.uSceneDist, uShadow: { value: null },
    uTargetSize: { value: new THREE.Vector2(1, 1) }, uLowSize: resolveU.uLowSize, uDistSize: { value: new THREE.Vector2(1, 1) },
    uShadowOn: { value: new THREE.Vector4(1, CLOUD.shadowStrength, CLOUD.fade[0], CLOUD.fade[1]) },
    uDebug: { value: 0 }
  };
  // The shadow pass reads the field, so it takes the field's uniforms.
  const shadowMat = raw(SHADOW_FRAG, { ...U, uShadowOn: compU.uShadowOn });
  const compositeMat = new THREE.RawShaderMaterial({
    glslVersion: THREE.GLSL3, vertexShader: COMPOSITE_VERT, fragmentShader: COMPOSITE_FRAG, uniforms: compU,
    transparent: true, depthTest: false, depthWrite: false,
    blending: THREE.CustomBlending, blendEquation: THREE.AddEquation,
    blendSrc: THREE.OneFactor, blendDst: THREE.SrcAlphaFactor,
    blendSrcAlpha: THREE.ZeroFactor, blendDstAlpha: THREE.OneFactor
  });
  const stampU = {
    uCloudA: compU.uCloudA, uCloudB: compU.uCloudB, uTargetSize: compU.uTargetSize, uLowSize: resolveU.uLowSize,
    uInvProj: U.uInvProj, uProjZ: { value: new THREE.Vector2() }
  };
  const stampMat = new THREE.RawShaderMaterial({
    glslVersion: THREE.GLSL3, vertexShader: COMPOSITE_VERT, fragmentShader: STAMP_FRAG, uniforms: stampU,
    transparent: true, depthTest: true, depthWrite: true, colorWrite: false
  });
  // Straight after the terrain's transparent outer ring (renderOrder -1) and
  // before every other transparent object.
  const compMesh = new THREE.Mesh(quadGeo, compositeMat);
  const stampMesh = new THREE.Mesh(quadGeo, stampMat);
  for (const [m, order, name] of [[compMesh, -0.9, 'volCloudsComposite'], [stampMesh, -0.8, 'volCloudsDepth']]) {
    m.frustumCulled = false;
    m.renderOrder = order;
    m.name = name;
    m.visible = false;
    m.userData.noCloudDepth = true;
    localScene.add(m);
  }

  const prepassMat = G.cloudPrepassMaterial || new THREE.ShaderMaterial({ vertexShader: PREPASS_VERT, fragmentShader: PREPASS_FRAG, side: THREE.FrontSide });

  // The tops cascades (TOPS above). The globe's material owns the cloud map
  // and how it is read: rocketry's Earth, or the viewer's.
  const blankTex = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1);
  blankTex.needsUpdate = true;
  U.uTops0.value = U.uTops1.value = U.uTops2.value = blankTex;
  const topsU = {
    ...cloudImageryUniforms,
    uClouds: { value: null }, uTexel: { value: new THREE.Vector2(1 / 8192, 1 / 4096) }, uSharpen: { value: 0.32 },
    uOpacity: { value: 0.85 }, uWeatherTime: { value: 0 },
    uLat0: { value: 0 }, uLon0: { value: 0 }, uStep: { value: 0 }, uSize: { value: TOPS.size }, uTexelM: { value: 0 },
    uCloudOrbitVol: { value: null }, uCloudVolOn: { value: 1 }, uCloudTopQuality: { value: 1 },
    uSunFixed: { value: new THREE.Vector3(0, 1, 0) }
  };
  const topsMat = new THREE.ShaderMaterial({
    vertexShader: 'varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }',
    fragmentShader: topsFrag(), uniforms: topsU, depthTest: false, depthWrite: false
  });
  let topsTargets = null;
  function earthUniforms() {
    const e = bodies.get('earth');
    const mesh = e.spinMesh || (ctx.getEarthMesh && ctx.getEarthMesh());
    const u = mesh && mesh.material && mesh.material.uniforms;
    return u && u.cloudsTexture && u.cloudsTexture.value ? u : null;
  }
  // Render the cascades for the camera at camSurf (Earth-fixed metres), or
  // turn them off; true when the volume can read them.
  let topsWereOn = false;
  function updateTops(camSurf, weight) {
    const eu = weight > 0 ? earthUniforms() : null;
    U.uTopsOn.value = 0;
    if (!eu || !noise) { topsWereOn = false; return false; }
    if (!topsTargets) {
      topsTargets = TOPS.texel.map(() => new THREE.WebGLRenderTarget(TOPS.size, TOPS.size, {
        format: THREE.RGFormat, type: THREE.UnsignedByteType, depthBuffer: false, stencilBuffer: false,
        minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter, generateMipmaps: true,
        wrapS: THREE.ClampToEdgeWrapping, wrapT: THREE.ClampToEdgeWrapping
      }));
      U.uTops0.value = topsTargets[0].texture;
      U.uTops1.value = topsTargets[1].texture;
      U.uTops2.value = topsTargets[2].texture;
    }
    const r = camSurf.length();
    const lat = Math.asin(THREE.MathUtils.clamp(camSurf.y / r, -1, 1)), lon = Math.atan2(-camSurf.z, camSurf.x);
    topsU.uClouds.value = eu.cloudsTexture.value;
    if (eu.cloudTexelSize) topsU.uTexel.value.copy(eu.cloudTexelSize.value);
    if (eu.cloudDetailStrength) topsU.uSharpen.value = eu.cloudDetailStrength.value;
    if (eu.cloudOpacity) topsU.uOpacity.value = eu.cloudOpacity.value;
    topsU.uWeatherTime.value = eu.weatherTime ? eu.weatherTime.value : 0;
    topsU.uCloudTopQuality.value = eu.uCloudTopQuality ? eu.uCloudTopQuality.value : 1;
    topsU.uCloudOrbitVol.value = noise.orbit.texture;
    surfaceOf(G.env.state.sunDir, topsU.uSunFixed.value).normalize();
    // The carve changes as slowly as the weather drifts (a few metres a
    // second against texels of 200 m and more), so the fine cascade is
    // redrawn every other frame and the coarse ones in turn between, each
    // sooner if the camera has moved a sixteenth of its window from the
    // centre it was drawn round. The volume reads each at the window it was
    // drawn with, so a cascade a frame old is still the right place.
    const fresh = !topsWereOn;
    for (let i = 0; i < 3; i++) {
      // The window's centre, to the nearest whole step: the lattice is one
      // and the same for every frame.
      const step = U.uTopsStep.value.getComponent(i);
      const lat0 = Math.round(lat / step) * step, lon0 = Math.round(lon / step) * step;
      const moved = Math.max(Math.abs(lat0 - U.uTopsLat0.value.getComponent(i)),
        Math.abs(Math.atan2(Math.sin(lon0 - U.uTopsLon0.value.getComponent(i)), Math.cos(lon0 - U.uTopsLon0.value.getComponent(i))))) / step;
      const due = i === 0 ? frame % 2 === 0 : frame % 8 === (i === 1 ? 1 : 5);
      if (!fresh && !due && moved < TOPS.size / 16) continue;
      U.uTopsLat0.value.setComponent(i, lat0);
      U.uTopsLon0.value.setComponent(i, lon0);
      topsU.uLat0.value = lat0;
      topsU.uLon0.value = lon0;
      topsU.uStep.value = step;
      topsU.uTexelM.value = TOPS.texel[i];
      pass(topsMat, topsTargets[i]);
    }
    U.uTopsOn.value = 1;
    topsWereOn = true;
    return true;
  }

  // Render targets, sized from the composer's own targets.
  let noise = null;
  let targets = null;
  let tierKey = '';
  let historyValid = false;
  let frame = 0;
  const stats = { active: false, reason: 'init', ms: 0, prepassObjects: 0, hidden: 0, low: [0, 0], full: [0, 0] };
  const debug = { mode: 0, force: null, hideDecks: false, off: false, noTops: false };

  function makeMRT(w, h, filter) {
    const t = new THREE.WebGLMultipleRenderTargets(w, h, 2, { depthBuffer: false, stencilBuffer: false });
    for (const [i, tex] of t.texture.entries()) {
      // Orbital depth must distinguish a kilometre-thick cloud from Earth
      // millions of metres away. Half floats round both to the same plane.
      tex.type = i === 1 ? THREE.FloatType : THREE.HalfFloatType;
      tex.format = THREE.RGBAFormat;
      tex.minFilter = tex.magFilter = filter;
      tex.generateMipmaps = false;
      tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
    }
    return t;
  }
  function disposeTargets() {
    if (!targets) return;
    for (const t of [targets.depth, targets.march, targets.histA, targets.histB, targets.shadow]) t.dispose();
    targets = null;
  }
  function ensureTargets(tier) {
    const rb = ctx.composer && ctx.composer.readBuffer;
    const fw = rb ? rb.width : renderer.domElement.width;
    const fh = rb ? rb.height : renderer.domElement.height;
    // March resolution: the tier's divisor, raised on very large buffers so
    // the ray count stays inside the budget.
    const budget = tier.volRays || 560000;
    let k = tier.volScale || 2;
    while ((fw / k) * (fh / k) > budget && k < 8) k++;
    const lw = Math.ceil(fw / k), lh = Math.ceil(fh / k);
    const key = [fw, fh, k].join('x');
    if (targets && key === tierKey) return;
    disposeTargets();
    tierKey = key;
    const floatOK = renderer.extensions.has('EXT_color_buffer_float');
    targets = {
      depth: new THREE.WebGLRenderTarget(fw, fh, {
        type: floatOK ? THREE.FloatType : THREE.HalfFloatType, format: THREE.RedFormat,
        minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, depthBuffer: true, stencilBuffer: false, generateMipmaps: false
      }),
      march: makeMRT(lw, lh, THREE.NearestFilter),
      histA: makeMRT(lw, lh, THREE.LinearFilter),
      histB: makeMRT(lw, lh, THREE.LinearFilter),
      shadow: new THREE.WebGLRenderTarget(lw, lh, {
        type: THREE.HalfFloatType, format: THREE.RGFormat, depthBuffer: false, stencilBuffer: false,
        minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, generateMipmaps: false
      }),
      fw, fh, lw, lh, k
    };
    U.uFullSize.value.set(fw, fh);
    U.uK.value = k;
    resolveU.uLowSize.value.set(lw, lh);
    compU.uDistSize.value.set(fw, fh);
    historyValid = false;
    stats.low = [lw, lh];
    stats.full = [fw, fh];
  }

  function ensureNoise() {
    if (noise) return;
    noise = buildCloudNoise(renderer);
    U.uWeather.value = noise.weather.texture;
    U.uShape.value = noise.shape.texture;
    U.uDetail.value = noise.detail.texture;
    stats.fit = noise.fit;
    // The tops pass carries the whole cloud-top chunk: compile it with the
    // bake (a moment the volume already waits for) so its first frame does
    // not stall.
    fsQuad.material = topsMat;
    renderer.compile(fsScene, fsCam);
  }

  // ---------------------------------------------------------------------------
  // Per-frame state
  // ---------------------------------------------------------------------------
  const _m4 = new THREE.Matrix4();
  const _m4b = new THREE.Matrix4();
  const _v = new THREE.Vector3();
  const _v2 = new THREE.Vector3();
  const _c = new THREE.Color();
  const _t = { r: 1, g: 1, b: 1 };
  const _sky = new THREE.Color();
  const _camSurf = new THREE.Vector3();
  const _drift = new THREE.Vector3();
  const _farClear = new Float32Array([FAR_CLEAR, 0, 0, 1]);
  const prev = { viewToSurf: new THREE.Matrix3(), proj: new THREE.Matrix4(), cam: { x: 0, y: 0, z: 0 }, time: 0, valid: false };
  const cur = { time: 0 };

  function surfaceOf(v, out) {
    const th = bodies.get('earth').spin;
    const c = Math.cos(th), s = Math.sin(th);
    return out.set(v.x * c - v.z * s, v.y, v.x * s + v.z * c);
  }

  // Coverage fade and the deck handoff for this frame; 0 means no volume.
  function volumeAmount(camAlt) {
    if (G.globalClouds) return 1 - THREE.MathUtils.smoothstep(camAlt, GLOBAL_VOLUME_FADE[0], GLOBAL_VOLUME_FADE[1]);
    return 1 - THREE.MathUtils.smoothstep(camAlt, CLOUD.altFade[0], CLOUD.altFade[1]);
  }

  function setDecks(amount) {
    const f = U.uFade.value;
    const weatherTime = bodies.get('earth')?.spinMesh?.material?.uniforms?.weatherTime?.value || 0;
    for (const rec of R.patches.values()) {
      if (rec.clouds && rec.clouds.setMapShift) rec.clouds.setMapShift(weatherTime);
      if (rec.clouds && rec.clouds.setVolumeHandoff) rec.clouds.setVolumeHandoff(amount, f.x, f.y, !!G.settings.tier().volClouds && !debug.off && rec.key === 'earth');
      if (rec.clouds && debug.hideDecks) rec.clouds.setVisible(false);
    }
  }

  function inactive(reason) {
    stats.active = false;
    stats.reason = reason;
    U.uTopsOn.value = 0;
    topsWereOn = false;
    compMesh.visible = false;
    stampMesh.visible = false;
    historyValid = false;
    setDecks(0);
  }

  // Where the field is: the camera's place in it, each field's wind drift,
  // and the cloud parameters. The cumulus deck reads the same uniforms, so
  // this runs every Earth frame whether or not the volume itself is on.
  function updateField(camSurf) {
    const camR = camSurf.length();
    U.uUp.value.copy(camSurf).divideScalar(camR);
    U.uCamR.value = camR;
    U.uCamAlt.value = camR - earthR;
    U.uToSurf.value.setFromMatrix4(_m4.makeRotationY(-bodies.get('earth').spin));
    // Absolute position of the camera (and each field's wind drift) folded
    // into [0,1) in double precision.
    const jd = ctx.getSimulationJD();
    const time = (jd - 2461000.5) * 86400;
    cur.time = time;
    const off = (out, tile, wind, base) => out.set(
      frac((base.x - wind.x * time) / tile), frac((base.y - wind.y * time) / tile), frac((base.z - wind.z * time) / tile));
    // The weather lookup is compressed along the wind, so its offset is too.
    _drift.copy(camSurf).addScaledVector(WIND.weather, -time);
    alongFold(_drift, _drift);
    U.uWeatherOff.value.set(frac(_drift.x / CLOUD.tiles[0]), frac(_drift.y / CLOUD.tiles[0]), frac(_drift.z / CLOUD.tiles[0]));
    off(U.uShapeOff.value, CLOUD.tiles[1], WIND.shape, camSurf);
    off(U.uDetailOff.value, CLOUD.tiles[2], WIND.detail, camSurf);
    off(U.uCurlOff.value, CLOUD.curlPeriod, WIND.detail, camSurf);
    U.uCurl.value.set(CLOUD.curl, CLOUD.curlPeriod, .9);
    U.uTiles.value.set(...CLOUD.tiles);
    U.uField.value.set(CLOUD.along, CLOUD.shear);
    U.uCloud.value.set(debug.force && debug.force.coverage != null ? debug.force.coverage : CLOUD.coverage, CLOUD.base, CLOUD.depthMin, CLOUD.depthMax);
    U.uCloud2.value.set(CLOUD.mapGain, CLOUD.baseVar, CLOUD.erosion, CLOUD.cloudType);
  }

  // Point the Earth patch's cumulus deck at the field while the volume is on,
  // so the two show the same clouds where they meet. Without the volume
  // (the low tier) the deck keeps its own noise, which reads better as the
  // only cloud there is than the field's bare domes do.
  const linked = new WeakMap();
  function linkDecks(on) {
    for (const rec of R.patches.values()) {
      if (!rec.clouds || !rec.clouds.setWeather) continue;
      const want = on && rec.key === 'earth';
      if (linked.get(rec.clouds) === want) continue;
      rec.clouds.setWeather(want ? U : null);
      linked.set(rec.clouds, want);
    }
  }

  function updateUniforms(tier, camSurf, amount) {
    const env = G.env;
    const camR = camSurf.length();
    const camAlt = camR - earthR;
    const up = _v.copy(U.uUp.value);
    U.uOpacity.value = amount;
    // Farthest point of the cloud shell visible around the solid Earth.
    // Fade bounds sit beyond it, so a distance circle cannot cross the image.
    const tangent = Math.sqrt(Math.max(0, camAlt * (2 * earthR + camAlt)))
      + Math.sqrt(CLOUD.layer[1] * (2 * earthR + CLOUD.layer[1]));
    U.uFade.value.set(tangent + 10000, tangent + 20000);
    // From orbit the volume draws the tops' clouds with their opacity and
    // light and takes their luminance shoulder (cloudshaders.js); near the
    // ground it keeps the full range of a sunlit cumulus. The same band the
    // cover follows the map over in rocketry, eased in fast: the near field
    // it replaces is an opaque slab that a tenth of its density still draws
    // solid, so a linear ramp held the flat cutouts to 75 km.
    U.uFarLook.value = 1 - (1 - THREE.MathUtils.smoothstep(camAlt, 30000, 80000)) ** 3;
    const r2 = camR * camR;
    const rb = earthR + CLOUD.layer[0], rt = earthR + CLOUD.layer[1];
    U.uSphereC.value.set((camR - rb) * (camR + rb), (camR - rt) * (camR + rt), (camR - earthR) * (camR + earthR), r2);

    // View to surface axes, and the camera's projection.
    localCamera.updateMatrixWorld();
    _m4.makeRotationY(-bodies.get('earth').spin).multiply(_m4b.extractRotation(localCamera.matrixWorld));
    U.uViewToSurf.value.setFromMatrix4(_m4);
    U.uInvProj.value.copy(localCamera.projectionMatrixInverse);
    const pe = localCamera.projectionMatrix.elements;
    stampU.uProjZ.value.set(pe[10], pe[14]);
    U.uPixelAngle.value = 2 * Math.atan(1 / localCamera.projectionMatrix.elements[5]) / Math.max(1, targets.fh);
    U.uMapPixelAngle.value = U.uPixelAngle.value * targets.k;

    U.uSigma.value = CLOUD.sigma;
    U.uDetailOn.value = tier.volDetail === false ? 0 : 1;
    // Primary budget, minimum step, growth per metre into the slab.
    U.uSteps.value.set(tier.volSteps || 96, 3, 0.0003, 1200);
    U.uLightSteps.value = tier.volLightSteps || 8;
    U.uMS.value.set(...CLOUD.ms);
    U.uMSGain.value = CLOUD.msGain;
    U.uPhase.value.set(...CLOUD.phase);
    U.uSilver.value = CLOUD.silver;

    // Where the camera is on the orbital cloud map, and how that address
    // changes with position (for the per-sample regional coverage).
    const map = getEarthWeather(renderer);
    U.uMap.value = map || null;
    U.uMapOn.value = map ? 1 : 0;
    {
      const x = camSurf.x, y = camSurf.y, z = camSurf.z;
      const rxz2 = x * x + z * z;
      const lat = Math.asin(THREE.MathUtils.clamp(y / camR, -1, 1));
      const lon = Math.atan2(-z, x);
      U.uMapUV.value.set(lon / (2 * Math.PI) + 0.5, 0.5 + lat / Math.PI);
      U.uDuDp.value.set(z / rxz2, 0, -x / rxz2).multiplyScalar(1 / (2 * Math.PI));
      const cl = Math.max(Math.cos(lat), 1e-4);
      const sl = Math.sin(lat);
      U.uDvDp.value.set(-sl * up.x, 1 - sl * up.y, -sl * up.z).multiplyScalar(1 / (camR * cl * Math.PI));
    }

    // Key light: the sun through the air, or the moon once environment.js
    // has swung its key light to it (both are near zero at the swap).
    const st = env.state;
    const sunSurf = surfaceOf(st.sunDir, _v2).normalize();
    const sunMu = sunSurf.dot(up);
    let lightDir;
    if (st.keyIsMoon && st.keyDir) {
      lightDir = surfaceOf(st.keyDir, U.uLightDir.value).normalize();
      airTransmittance(Math.max(camAlt, 0), lightDir.dot(up), earthR, _t);
      const k = env.sunLight.intensity;
      _c.copy(env.sunLight.color);
      U.uLightE.value.set(_c.r * k / Math.max(_t.r, 1e-3), _c.g * k / Math.max(_t.g, 1e-3), _c.b * k / Math.max(_t.b, 1e-3));
    } else {
      lightDir = U.uLightDir.value.copy(sunSurf);
      airTransmittance(0, 1, earthR, _t);
      const k = CLOUD.sunIntensity;
      U.uLightE.value.set(SUN_WARM.r * k / _t.r, SUN_WARM.g * k / _t.g, SUN_WARM.b * k / _t.b);
    }
    // Cone basis around the light.
    const T = U.uLightT.value.set(0, 1, 0).cross(lightDir);
    if (T.lengthSq() < 1e-6) T.set(1, 0, 0).cross(lightDir);
    T.normalize();
    U.uLightB.value.crossVectors(lightDir, T).normalize();

    // Ambient: the sky above the layer and the lit ground below it, with the
    // hemisphere light's night floor so night cloud stays readable. The tops
    // take the blue of the zenith; the shadowed undersides take skylight,
    // which is cool blue at midday and turns violet as the sun drops. That
    // relationship is what makes a warm sunlit face read pink at sunset
    // rather than orange, so the tint has to lift blue back over green at
    // the end (TAKEN's shadow tint).
    approximateSkyColor(2500, sunMu, _sky);
    const hemi = env.hemi;
    const hk = hemi.intensity * 0.1;
    U.uAmbTop.value.set(
      _sky.r * CLOUD.ambSky * 0.90 + hemi.color.r * hk,
      _sky.g * CLOUD.ambSky * 0.96 + hemi.color.g * hk,
      _sky.b * CLOUD.ambSky * 1.10 + hemi.color.b * hk);
    airTransmittance(0, Math.max(sunMu, 0.02), earthR, _t);
    const sunGround = st.keyIsMoon ? 0 : CLOUD.sunIntensity * Math.max(sunMu, 0) * THREE.MathUtils.smoothstep(sunMu, -0.02, 0.08);
    const gk = CLOUD.groundAlbedo * CLOUD.ambGround / Math.PI;
    const night = 1 - st.dayLight;
    const low = 1 - THREE.MathUtils.smoothstep(sunMu, 0.02, 0.26);
    const shR = 0.72 + 0.34 * low, shG = 0.86 - 0.02 * low, shB = 1.18 - 0.20 * low;
    // window.__rkNightLampsOff draws the old constant glow, for before and
    // after captures.
    const legacy = globalThis.__rkNightLampsOff ? 1 : 0;
    const oldGlow = night * legacy;
    // globalThis.__earthLights: the viewer's city lights (whatiffuture.js
    // lights), 0 for an Earth with nobody on it.
    const glow = night * (1 - legacy) * (globalThis.__earthLights ?? 1) / CLOUD.cityGlowRef;
    U.uCityGlow.value.set(CLOUD.cityGlow[0] * glow, CLOUD.cityGlow[1] * glow, CLOUD.cityGlow[2] * glow, 0.5);
    U.uAmbBottom.value.set(
      gk * (sunGround * _t.r + _sky.r * 1.5 * shR) + hemi.groundColor.r * hk + CLOUD.cityGlow[0] * oldGlow,
      gk * (sunGround * _t.g + _sky.g * 1.5 * shG) + hemi.groundColor.g * hk + CLOUD.cityGlow[1] * oldGlow,
      gk * (sunGround * _t.b + _sky.b * 1.5 * shB) + hemi.groundColor.b * hk + CLOUD.cityGlow[2] * oldGlow);

    // Aerial perspective: the terrain's fog carrier (environment.js).
    const fog = localScene.fog;
    if (fog && fog.isFog) {
      U.uFog.value.set(1, fog.far * 1e-6, (st.hazeScaleHeight) || 2000, Math.max(fog.near, 0));
      U.uFogColor.value.copy(fog.color);
    } else U.uFog.value.set(0, 0, 2000, 0);
    U.uFogSun.value.copy(lightDir);
    // The far look's light is the tops' (cloudFarLight), which is sunlight:
    // it hands back to the volume's own light as the sun sets at the camera,
    // before environment.js swings the key light to the moon.
    U.uFarColour.value = st.keyIsMoon ? 0 : U.uFarLook.value * THREE.MathUtils.smoothstep(sunMu, -0.02, 0.06);

    // In the normal viewer Earth's surface shader owns projected cloud
    // shadows at every zoom. The local envelope shadow would otherwise
    // darken the already-shaded globe (including its atmospheric haze).
    // At orbital footprints its wide soft threshold even shades clear sky.
    // Through the hand-off the orbital shell casts its own shadow, faded in
    // as the volume fades out, so the volume's goes with its opacity (amount)
    // and the two never darken the ground twice. Seen from orbit the shadow
    // reaches as far as the clouds do, as the shell's does: it is read off
    // the tops' smooth opacity there, which has nothing in it to alias at
    // range.
    const localShadows = !G.globalClouds && !st.keyIsMoon;
    const far = U.uFarLook.value;
    compU.uShadowOn.value.set(localShadows ? 1 : 0, localShadows ? CLOUD.shadowStrength * THREE.MathUtils.smoothstep(sunMu, 0.0, 0.12) * amount : 0,
      G.globalClouds ? tangent + 10000 : THREE.MathUtils.lerp(CLOUD.fade[0], tangent + 10000, far),
      G.globalClouds ? tangent + 20000 : THREE.MathUtils.lerp(CLOUD.fade[1], tangent + 20000, far));
  }

  // ---------------------------------------------------------------------------
  // Passes
  // ---------------------------------------------------------------------------
  const hidden = [];
  function writesDepth(m) {
    if (Array.isArray(m)) return m.some(writesDepth);
    return !!m && m.visible !== false && m.depthWrite && m.depthTest && m.colorWrite !== false;
  }

  // The depth proxies (depth.js) stand in for the vessels' and the launch
  // complex's own meshes here, so the pass draws one merged geometry per part
  // and per facility instead of one per surface.
  function prepass() {
    withDepthLayer(localCamera, () => {
      hidden.length = 0;
      let drawn = 0;
      localScene.traverseVisible((o) => {
        if (!(o.isMesh || o.isPoints || o.isLine || o.isSprite)) return;
        if (o.userData.noCloudDepth || !o.isMesh || !writesDepth(o.material)) hidden.push(o);
        else drawn++;
      });
      for (const o of hidden) o.visible = false;
      const sm = renderer.shadowMap;
      const prevAuto = sm.autoUpdate, prevNeeds = sm.needsUpdate;
      sm.autoUpdate = false; sm.needsUpdate = false;
      const prevOverride = localScene.overrideMaterial;
      localScene.overrideMaterial = prepassMat;
      renderer.setRenderTarget(targets.depth);
      const gl = renderer.getContext();
      renderer.state.buffers.color.setMask(true);
      renderer.state.buffers.depth.setMask(true);
      gl.clearBufferfv(gl.COLOR, 0, _farClear);
      renderer.clearDepth();
      renderer.render(localScene, localCamera);
      localScene.overrideMaterial = prevOverride;
      sm.autoUpdate = prevAuto; sm.needsUpdate = prevNeeds;
      for (const o of hidden) o.visible = true;
      stats.prepassObjects = drawn;
      stats.hidden = hidden.length;
    }, { hideSources: true });
  }

  function pass(material, target) {
    fsQuad.material = material;
    renderer.setRenderTarget(target);
    renderer.render(fsScene, fsCam);
  }

  function onFrame() {
    if (!R.active) return;
    const tier = G.settings.tier();
    if (R.mode === 'vab' || R.mapMode) { inactive(R.mode === 'vab' ? 'vab' : 'map'); return; }
    if (R.viewBody !== 'earth') { inactive('body'); return; }
    const t0 = performance.now();
    const camSurf = surfaceOf(camInertial, _camSurf);
    linkDecks(!!tier.volClouds && !debug.off);
    if (!tier.volClouds || debug.off) { inactive(debug.off ? 'off' : 'tier'); return; }
    ensureNoise();
    updateField(camSurf);
    const camAlt = camSurf.length() - earthR;
    const amount = volumeAmount(camAlt);
    if (amount <= 0.001) { inactive('altitude'); return; }
    ensureTargets(tier);
    stats.active = true;
    stats.reason = '';

    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    renderer.autoClear = false;

    // The tops' cover, wherever the volume follows the map (weatherReg's
    // toMap: always in the viewer, from 30 km up in rocketry).
    stats.tops = updateTops(camSurf, debug.noTops ? 0 : G.globalClouds ? 1 : THREE.MathUtils.smoothstep(camAlt, 30000, 80000));
    updateUniforms(tier, camSurf, amount);
    setDecks(amount);
    // History is only meaningful if the camera did not cut.
    const dx = camSurf.x - prev.cam.x, dy = camSurf.y - prev.cam.y, dz = camSurf.z - prev.cam.z;
    const jump = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (!prev.valid || jump > Math.max(3000, camAlt * .025)) historyValid = false;

    prepass();
    U.uSceneDist.value = targets.depth.texture;

    // The cloud shadow on this frame's surfaces.
    pass(shadowMat, targets.shadow);
    compU.uShadow.value = targets.shadow.texture;

    // March with this frame's sub-texel jitter.
    const j = HALTON[frame % (tier.volJitter || 8)];
    // Once every ray uses the filtered orbital column, shifting the sample
    // each frame only makes its resolved weather map crawl. Centre those
    // rays and retire history continuously over the same footprint range
    // used by MARCH_FRAG. Near clouds retain temporal supersampling.
    const orbitalResolve = THREE.MathUtils.smoothstep(
      Math.max(camAlt - CLOUD.layer[1], 0) * U.uMapPixelAngle.value,
      CLOUD.tiles[1] * 0.70, CLOUD.tiles[1] * 1.40);
    U.uJitter.value.set(THREE.MathUtils.lerp(j[0], 0.5, orbitalResolve),
      THREE.MathUtils.lerp(j[1], 0.5, orbitalResolve));
    U.uFrame.value = frame % 64;
    pass(marchMat, targets.march);

    // Resolve into the history.
    const read = targets.histA, write = targets.histB;
    resolveU.uCurA.value = targets.march.texture[0];
    resolveU.uCurB.value = targets.march.texture[1];
    resolveU.uHistA.value = read.texture[0];
    resolveU.uHistB.value = read.texture[1];
    resolveU.uHistoryValid.value = historyValid ? 1 : 0;
    resolveU.uAlpha.value = THREE.MathUtils.lerp(tier.volAlpha || 0.1, 1, orbitalResolve);
    resolveU.uPrevSurfToView.value.copy(prev.viewToSurf).transpose();
    resolveU.uPrevProj.value.copy(prev.proj);
    resolveU.uCamDelta.value.set(dx, dy, dz);
    const dtw = cur.time - prev.time;
    resolveU.uWindStep.value.copy(WIND.shape).multiplyScalar(Number.isFinite(dtw) && Math.abs(dtw) < 60 ? dtw : 0);
    pass(resolveMat, write);
    targets.histA = write;
    targets.histB = read;
    historyValid = true;

    // Debug mode 3 composites this frame's raw march, bypassing the history.
    const shown = debug.mode === 3 ? targets.march : write;
    compU.uCloudA.value = shown.texture[0];
    compU.uCloudB.value = shown.texture[1];
    compU.uTargetSize.value.set(targets.fw, targets.fh);
    compU.uDebug.value = debug.mode === 3 ? 0 : debug.mode;
    compMesh.visible = true;
    stampMesh.visible = debug.mode === 0;

    prev.viewToSurf.copy(U.uViewToSurf.value);
    prev.proj.copy(localCamera.projectionMatrix);
    prev.cam.x = camSurf.x; prev.cam.y = camSurf.y; prev.cam.z = camSurf.z;
    prev.time = cur.time;
    prev.valid = true;

    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAutoClear;
    frame++;
    stats.ms = performance.now() - t0;
  }

  const offFrame = G.events.on('frame', onFrame);
  // Leaving the mode frees the render targets (about 40 MB at 1080p); the
  // noise volumes are kept, they take a moment to bake.
  const offExit = G.events.on('exit', () => { inactive('exit'); disposeTargets(); tierKey = ''; });
  const offMode = G.events.on('mode', () => { historyValid = false; prev.valid = false; debug.force = null; });
  const offMap = G.events.on('map', () => { historyValid = false; prev.valid = false; });
  const offSettings = G.events.on('settings', ({ key }) => {
    if (key !== 'quality') return;
    historyValid = false;
    tierKey = '';
    if (!G.settings.tier().volClouds) disposeTargets();
  });

  // Debug and tuning handle (reachable from the capture harness through
  // window.__debug.rocketry._G.volClouds on localhost).
  const api = {
    CLOUD,
    stats: () => ({ ...stats, tier: tierKey }),
    uniforms: U,
    // Whether the volume is carving the cloud tops' cells this frame (the
    // cloud pages are streamed for it: cloudimagery.js).
    topsActive: () => U.uTopsOn.value > 0.5,
    // The tops' cells off (the volume's own placement at the map's cover),
    // for an A/B.
    setTops(on = true) { debug.noTops = !on; },
    set(params = {}) { Object.assign(CLOUD, params); historyValid = false; },
    setDebug(mode) { debug.mode = mode | 0; },
    setMarchDebug(mode) { U.uMarchDebug.value = mode | 0; },
    hideDecks(on = true) { debug.hideDecks = !!on; },
    setEnabled(on = true) { debug.off = !on; },
    force(o) { debug.force = o; },
    reset() { historyValid = false; },
    // Linear cloud radiance and transmittance at a screen position (0..1,
    // origin bottom left), from the resolved history.
    probe(x, y) {
      if (!targets) return null;
      const px = Math.min(targets.lw - 1, Math.floor(x * targets.lw)), py = Math.min(targets.lh - 1, Math.floor(y * targets.lh));
      // three's readRenderTargetPixels does not accept multiple render
      // targets, so read attachment 0 directly and let three rebind.
      const gl = renderer.getContext();
      const fb = renderer.properties.get(targets.histA).__webglFramebuffer;
      if (!fb) return null;
      renderer.state.bindFramebuffer(gl.FRAMEBUFFER, fb);
      const buf = new Float32Array(4), bufB = new Float32Array(4);
      gl.readBuffer(gl.COLOR_ATTACHMENT0);
      gl.readPixels(px, py, 1, 1, gl.RGBA, gl.FLOAT, buf);
      gl.readBuffer(gl.COLOR_ATTACHMENT1);
      gl.readPixels(px, py, 1, 1, gl.RGBA, gl.FLOAT, bufB);
      gl.readBuffer(gl.COLOR_ATTACHMENT0);
      renderer.setRenderTarget(renderer.getRenderTarget());
      // [L.r, L.g, L.b, T, entry km, exit km, representative km, scene km]
      return [...Array.from(buf), ...Array.from(bufB)].map((v) => +v.toFixed(3));
    },
    dispose
  };
  G.volClouds = api;

  function dispose() {
    offFrame(); offExit(); offMode(); offMap(); offSettings();
    localScene.remove(compMesh);
    localScene.remove(stampMesh);
    disposeTargets();
    if (noise) { noise.dispose(); noise = null; }
    if (topsTargets) for (const t of topsTargets) t.dispose();
    topsTargets = null;
    blankTex.dispose();
    quadGeo.dispose();
    for (const m of [marchMat, shadowMat, resolveMat, compositeMat, stampMat, prepassMat, topsMat]) m.dispose();
    if (G.volClouds === api) G.volClouds = null;
  }

  return { dispose };
}
